import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { scopeKindOf } from "./settings/scope";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Footer, FooterEnd } from "./footer/Footer";
import { Steps } from "./funnel/Steps";
import { OrganiserBar } from "./panel/OrganiserBar";
import { Band } from "./site/Band";
import { CustomCss } from "./site/CustomCss";
import { Overview } from "./panel/Overview";
import { SpotEditor } from "./panel/SpotEditor";
import { Panel } from "./panel/Panel";
import { SiteNav, SiteTabbar } from "./pages/nav";
import { REPEATABLE_PAGES, pagePath } from "./pages/registry";
import { isPublicScreen } from "./pages/publicPages";
import { isClosedAt } from "./pages/closedPages";
import { stepPageOf } from "./pages/stepPages";
import { ModeProvider, readerSignedIn } from "./panel/mode";
import {
  REPEATABLE_STEP,
  hasBanner,
  hasFooter,
  isBare,
  needsSession,
  screenOf,
  stripContext,
  withContext,
} from "./routes/registry";
import { instanceOf } from "./funnel/order";
import { isSignedIn, signOut } from "./auth/session";
import { usePhoto } from "./auth/photo";
import { hasAnswered, useAnswers } from "./funnel/answers";
import {
  CONFIRMATION,
  activeSteps,
  answersForParticipant,
  firstStep,
  inThread,
} from "./funnel/thread";
import {
  documentRestored,
  isEntry,
  isResumed,
  usePrevious,
} from "./funnel/entree";
import { useOffer } from "./funnel/useOffre";
import { comes, isRegistered } from "./funnel/participant";
import { useParticipation } from "./funnel/useParticipation";
import { viewerTypoOf } from "./funnel/viewerTypo";
import { Button } from "./ui/Button";
import { Confirm } from "./ui/Confirm";
import { Icon } from "./ui/Icon";
import { useEventState } from "./context/EventProvider";
import { useSession } from "./auth/useSession";
import { useSessionKey } from "./auth/useSessionKey";
import { useCanConfigure } from "./auth/useRights";
import { setOrganiserMode, usePendingSignIn } from "./log/actionLog";
import { useTheme } from "./theme/useTheme";
import { fallbackOf } from "./settings/catalog";
import { useSettings, useT, useText } from "./settings/context";
import { useDocumentTitle, useEventName } from "./settings/eventName";
import { fileUrl } from "./settings/fichier";
import { languageName } from "./settings/nomDeLangue";
import { DEFAULT_SITUATION } from "./panel/situation";
import { OrganiserViews } from "./panel/OrganiserViews";
import { NotFound } from "./routes/NotFound";
import { captureChannel } from "./funnel/registrationChannel";
import type { Situation } from "./settings/types";

/**
 * THE SHELL — the structure shared by every screen, and the TWO MODES.
 *
 * Visitor mode: the bare site, plus a discreet "configure" affordance.
 * Organiser mode: the same page, plus the tool's own bar on top and the settings
 * panel on the right. The bar says WHAT YOU ARE LOOKING AT (event, screen,
 * viewing typology, device); the panel says WHAT YOU ARE CHANGING.
 *
 * That is the whole bet of V2: you configure INSIDE the site, not in a back
 * office you must reload to see the effect.
 *
 * Two structural points inherited from the prototype, not to be undone:
 *   1. `.scroll` is a flex column and the footer carries `margin-top: auto` —
 *      that is what pins it to the bottom when content is short, mobile included.
 *   2. `.site` carries `container-type: inline-size`: layout responds to its
 *      CONTAINER, not to the window. Opening the panel therefore genuinely
 *      narrows the rendering — the only way to see what a visitor sees while
 *      configuring.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */

/** Time the pointer must rest on a block before its settings open. */
const OPEN_DELAY = 140;
/** Grace given to cross the gap between the block and its card. */
const CLOSE_DELAY = 260;
/** Le temps laissé au nouvel écran pour dessiner son titre (il attend souvent l'API). */
const TITLE_WAIT = 10_000;

/**
 * Pose le focus sur le titre de l'écran qui vient de s'ouvrir, s'il est perdu.
 *
 * ⚠️ SEULEMENT S'IL EST PERDU, c'est-à-dire sur le `body`. Le bouton « Continuer »
 * disparaît avec l'étape qu'on quitte et emporte le focus ; un lien du menu, lui,
 * reste là, et le garder sous la main vaut mieux que de le déplacer.
 *
 * ⚠️ ET LE TITRE PEUT ARRIVER APRÈS. La plupart des écrans attendent l'API avant de le
 * dessiner : on le guette, et on renonce dès que le focus est reparti ailleurs (le
 * visiteur a tabulé entre-temps) ou au bout de `TITLE_WAIT`. Rend de quoi arrêter.
 *
 * ⚠️ ET LE PREMIER TITRE N'EST PAS TOUJOURS LE BON (suite du bug #406). La confirmation
 * dessine d'abord « Enregistrement de votre réponse », le temps d'écrire, puis le
 * remplace par le sien : le focus posé sur le provisoire partait avec lui, et l'on
 * retombait sur le `body`. Le titre focalisé reste donc suivi : s'il disparaît en
 * gardant le focus, celui qui le remplace le reprend. On lâche dès que le visiteur a
 * quitté ce titre lui-même (Tab, un clic ailleurs).
 */
function focusTitleWhenLost(within: () => HTMLElement | null): () => void {
  /** Le titre focalisé ici, suivi tant que l'écran se met en place. */
  let held: HTMLElement | null = null;
  let timer = 0;
  /* `true` : plus rien à faire. */
  const attempt = () => {
    const active = document.activeElement;
    if (held && active === held) return false;
    if (active !== null && active !== document.body) return true;
    /* Encore là, mais quitté : le visiteur a cliqué dans le vide, on ne le lui reprend pas. */
    if (held?.isConnected) return true;
    const title = within()?.querySelector<HTMLElement>(".scroll__body h2");
    if (!title) return false;
    /* Un titre n'est pas focalisable de lui-même : `-1` le rend focalisable par le
       programme sans l'ajouter à l'ordre de tabulation. */
    if (!title.hasAttribute("tabindex")) title.tabIndex = -1;
    held = title;
    title.focus();
    /* Le délai repart : l'écriture qui remplacera ce titre peut être lente. */
    window.clearTimeout(timer);
    timer = window.setTimeout(() => stop(), TITLE_WAIT);
    return false;
  };
  const observer = new MutationObserver(() => {
    if (attempt()) stop();
  });
  /* Le focus que l'on pose soi-même n'arrête rien ; celui que le visiteur déplace, si. */
  const onFocusIn = (event: FocusEvent) => {
    if (event.target !== held) stop();
  };
  const stop = () => {
    observer.disconnect();
    window.clearTimeout(timer);
    document.removeEventListener("focusin", onFocusIn);
  };
  if (attempt()) return () => {};
  if (!held) timer = window.setTimeout(() => stop(), TITLE_WAIT);
  observer.observe(document.body, { childList: true, subtree: true });
  document.addEventListener("focusin", onFocusIn);
  return stop;
}

export function Shell() {
  const [mode, setMode] = useState<"visitor" | "organiser">("visitor");
  /**
   * ⚠️ LE MODE ORGANISATEUR EST RÉSERVÉ À QUI A LE DROIT D'ÉCRIRE CET ÉVÉNEMENT. Voir
   * `auth/useRights.ts`. Le bouton ne se montre qu'à lui, et si le droit tombe (une
   * déconnexion, un autre compte) le panneau se referme.
   */
  const canConfigure = useCanConfigure();
  useEffect(() => {
    if (mode === "organiser" && !canConfigure) setMode("visitor");
  }, [mode, canConfigure]);
  // Le journal de la fiche contact se tait tant que l'organisateur configure : `log/actionLog.ts`.
  useEffect(() => {
    setOrganiserMode(mode === "organiser");
    return () => setOrganiserMode(false);
  }, [mode]);
  // The preview format belongs to the SHELL, not to the bar: `.site` is narrowed
  // from here, and the bar only holds the control that flips it.
  const [view, setView] = useState<"desktop" | "mobile">("desktop");
  /**
   * ⚠️ L'APERÇU « VISITEUR NON CONNECTÉ » DE LA BARRE, et c'est une VUE, pas un
   * réglage : il ne se garde pas d'une session à l'autre et ne change rien à ce que
   * les participants voient. Voir `panel/mode.ts` pour ce qu'il vaut vraiment — les
   * requêtes partent toujours avec le jeton de l'organisateur.
   */
  const [asAnonymous, setAsAnonymous] = useState(false);
  /**
   * La version de l'écran de confirmation que l'organisateur regarde (sur place,
   * paiement en attente…). Une VUE aussi, comme la précédente : voir
   * `panel/situation.tsx`.
   */
  const [situation, setSituation] = useState<Situation>(DEFAULT_SITUATION);
  // The block whose settings are open beside it. An ELEMENT, not a name: the
  // card follows it as the page scrolls.
  const [spot, setSpot] = useState<HTMLElement | null>(null);
  const [recap, setRecap] = useState(false);
  /**
   * The site node held in STATE, not in a ref: the loading gate unmounts it, and a
   * ref's identity never changes, so nothing would tell the palette that a new node
   * had arrived to paint. See `useTheme`.
   */
  const [site, setSite] = useState<HTMLDivElement | null>(null);
  // The event's colours are painted on the site, never on the tool around it.
  useTheme(site);
  const openIn = useRef<number | undefined>(undefined);
  const closeIn = useRef<number | undefined>(undefined);
  const {
    pathname: rawPath,
    search: rawSearch,
    state: navState,
  } = useLocation();
  const navigate = useNavigate();
  const { context } = useParams();
  // Registration channel (`?canal=`), kept for the whole visit as V1 does.
  useEffect(() => {
    captureChannel(rawSearch);
  }, [rawSearch]);
  // Every lookup below reasons on the CANONICAL path. The event token is a
  // prefix and only `registry.ts` knows it — see `stripContext`.
  const pathname = stripContext(rawPath, context);
  /**
   * ═══ UNE NOUVELLE PAGE S'OUVRE EN HAUT (bug #343) ═══
   *
   * Le site défile dans `.scroll`, et `.scroll` appartient au châssis : il reste monté
   * d'une route à l'autre, et garde donc la hauteur de la page qu'on quitte. Depuis le
   * bas de la confirmation, « Voir mon badge » ouvrait le badge à mi-carte, titre et
   * dates hors de vue.
   *
   * ⚠️ SUR LE CHEMIN SEUL, pas sur `search` ni `hash` : une ancre de section
   * (`Infos.tsx`) ou un paramètre qui change ne sont pas une nouvelle page.
   * ⚠️ AVANT LE DESSIN (`useLayoutEffect`), sinon la nouvelle page se peint une fois
   * à l'ancienne hauteur avant de remonter.
   * ⚠️ LE BOUTON PRÉCÉDENT AUSSI : il n'y avait rien à garder, il héritait lui aussi de
   * la hauteur de la page quittée, pas de celle où l'on avait laissé la sienne.
   */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [rawPath]);
  /**
   * ═══ ET LE FOCUS ARRIVE SUR SON TITRE (bug #406) ═══
   *
   * Au clavier, chaque « Continuer » du tunnel laissait le focus sur le `body` : le
   * bouton part avec l'étape quittée, le lecteur d'écran ne disait pas qu'une autre
   * s'était ouverte, et la tabulation repartait de l'en-tête. Posé ICI, une fois pour
   * toutes les routes, et non dans chaque écran : c'est le châssis qui voit l'adresse
   * changer. Pas à la première adresse : à l'arrivée sur le site, rien n'a été perdu.
   * ⚠️ COMPARÉE À LA PRÉCÉDENTE, pas « premier passage ou non » : `StrictMode` rejoue
   * l'effet au montage, et ce second passage aurait volé le focus dès l'arrivée.
   */
  const servedPath = useRef(rawPath);
  useEffect(() => {
    if (servedPath.current === rawPath) return;
    servedPath.current = rawPath;
    return focusTitleWhenLost(() => scrollRef.current);
  }, [rawPath]);
  // The catalog scope comes from the REGISTRY, not from a second path-to-key rule
  // written here: two mappings for the same thing drift apart, and the symptom
  // would be a screen quietly losing its own settings tab.
  /**
   * ⚠️ LA MÊME ÉTAPE, SERVIE COMME PAGE (`/informations`) — voir `pages/stepPages.ts`.
   * Le panneau doit y trouver LES MÊMES RÉGLAGES qu'à l'étape : sinon « Cette page »
   * s'ouvre vide sur un écran qui en porte cinq, et l'organisateur ne peut régler son
   * formulaire que depuis le tunnel — c'est-à-dire depuis l'endroit qu'il vient
   * précisément de ne plus servir.
   */
  const servedAsPage = stepPageOf(pathname);
  const page = screenOf(pathname)?.key ?? servedAsPage?.step.key ?? "";
  /* L'instance que l'on regarde : `/registration/informations/<id>`, `/informations/<id>`
     hors du tunnel, ou `/infos/<id>` pour un exemplaire de la page multipliable. Le
     panneau en a besoin pour écrire dans les clés de CET écran-là ; sans elle, régler
     la page 2 écrirait dans la première. */
  const readInstance =
    (REPEATABLE_STEP
      ? instanceOf(pathname, REPEATABLE_STEP.path)
      : undefined) ??
    servedAsPage?.instance ??
    /* ⚠️ TOUTES LES FAMILLES MULTIPLIABLES, plus « la » page multipliable : la
       première qui reconnaît l'adresse la porte. Écrite au singulier, la ligne ne
       voyait que la section, et la page formulaire 2 réglait la 1 dans le panneau. */
    REPEATABLE_PAGES.map((page) => instanceOf(pathname, pagePath(page))).find(
      Boolean,
    );
  /**
   * ⚠️ ET CELLE-LÀ N'EST PAS DANS LE TUNNEL, quoi qu'en dise le catalogue. `inFunnel`
   * commande le châssis à deux colonnes, le fil d'Ariane et le garde d'entrée
   * (`funnel/entree.ts`) : lue depuis la page, ce dernier aurait renvoyé vers la
   * confirmation quiconque a déjà répondu — le lien du menu n'aurait jamais ouvert
   * l'écran promis. Elle se lit donc sur `screenOf` SEUL.
   */
  const inFunnel = !!screenOf(pathname)?.funnel;

  /**
   * NO SESSION, NO SCREEN. A participant's screens are their own; without a token
   * the visitor goes to sign in, and comes back to the address they were after
   * rather than being dumped on the home screen.
   *
   * ⚠️ ORGANISER MODE PASSES THROUGH, and that deserves saying out loud. It is
   * already the "I am an authorised organiser" mode with nothing to prove it —
   * the same gap as the missing server-side right, which is why writes still go
   * to a local store. Gating it here would only make a site impossible to
   * configure while changing nothing about who can enter it.
   */
  const signedIn = useSession();
  const event = useEventState();
  // La connexion par lien, notée dès que l'événement est connu : `log/actionLog.ts`.
  usePendingSignIn(event.kind === "ready" ? event.event : null);

  /**
   * UNE SURFACE DE PROJECTION, PAS UNE PAGE.
   *
   * ⚠️ L'ÉCRAN DE SALLE PORTAIT LE MOBILIER DU PARTICIPANT — menu, sélecteur de
   * langue, pied de page, bouton « Configurer le site » — au milieu d'une plénière,
   * sur dix mètres de large. Constaté en le projetant : le châssis est posé ici, donc
   * aucun test de l'écran lui-même ne pouvait le voir.
   */
  const bare = isBare(pathname);

  const t = useT();
  const { scope, languages, typos, chooseLanguage, setLanguage, ready, value } =
    useSettings();

  /**
   * ⚠️ SAUF LES PAGES QUE L'ORGANISATEUR A OUVERTES. L'accueil et les sections savent
   * se rendre sans session (elles ne lisent que la configuration, servie sans jeton) :
   * quand l'interrupteur est allumé, la porte reste ouverte. Voir `pages/publicPages`.
   *
   * ⚠️ LU AVEC L'INSTANCE, sinon toutes les sections auraient partagé le sort de la
   * première : ouvrir la section 2 aurait ouvert les trois.
   */
  const openToAll = isPublicScreen(
    screenOf(pathname)?.key,
    readInstance,
    value,
  );
  /* POUR QUI ON DESSINE, la règle unique : voir `panel/mode.ts`. Le châssis appelle la
     fonction pure et non le crochet, parce que c'est lui qui pose le fournisseur. */
  const reader = readerSignedIn(mode, signedIn, asAnonymous);
  const locked = needsSession(pathname) && !reader && !openToAll;
  /**
   * ⚠️ UNE PAGE FERMÉE N'EXISTE PLUS POUR LE PARTICIPANT, même par son adresse : voir
   * `closedKey`. Elle reste ouverte en mode organisateur, qui doit pouvoir la régler :
   * la même exception que `funnel/StepPage.tsx`.
   */
  const closed = mode === "visitor" && isClosedAt(pathname, value);

  /**
   * LEAVING THE FUNNEL — asked about only when it costs something.
   *
   * Answers given in this walk live in the navigation state and are written at the
   * confirmation, not before: leaving from step one loses them. So the question is
   * asked when there IS something to lose, and not when everything already sits on
   * the server — a dialog on every exit is friction, and friction teaches people to
   * dismiss dialogs without reading them.
   */
  const [asking, setAsking] = useState(false);
  const [answers, remembered] = useAnswers();
  const {
    recorded,
    me,
    loading: participationEnCours,
    settled: knownParticipation,
    sessionRefused,
    unavailable: identityUnreadable,
    retryIdentity,
    retrying: identityRetrying,
  } = useParticipation();

  /**
   * ═══ UN AUTRE COMPTE DANS UN AUTRE ONGLET : ON LE DIT (bug 8, chasse du 2026-10-20) ═══
   *
   * ⚠️ LE JETON EST PARTAGÉ PAR TOUS LES ONGLETS. Quand l'un ouvre une autre session, celui-ci
   * change de personne sous les yeux de qui le regarde — et il ne se passait RIEN de visible :
   * les valeurs saisies pour A restaient à l'écran pendant que les requêtes partaient au nom
   * de B. Les requêtes personnelles sont maintenant clefées sur la session (`useSessionKey`) :
   * l'écran se démonte, ce qui n'était pas enregistré est abandonné, et cette phrase dit
   * pourquoi, avant d'afficher le nouveau compte.
   *
   * ⚠️ UNIQUEMENT D'UN COMPTE À UN AUTRE. Se connecter (rien → jeton) ou se déconnecter
   * (jeton → rien) est une action de CET onglet, ou une déconnexion qui a son propre message.
   */
  const sessionKey = useSessionKey();
  const previousSession = useRef(sessionKey);
  const [switchedAccount, setSwitchedAccount] = useState(false);
  useEffect(() => {
    const before = previousSession.current;
    previousSession.current = sessionKey;
    if (before && sessionKey && before !== sessionKey) setSwitchedAccount(true);
  }, [sessionKey]);
  const switchedNotice = switchedAccount ? (
    <p className="session-switched" role="status">
      {me
        ? t(
            "SESSION_SWITCHED_TO",
            "Votre session a changé dans un autre onglet : vous êtes maintenant connecté en tant que $1. Ce qui n'avait pas été enregistré ici a été abandonné.",
            [`${me.firstName} ${me.lastName}`.trim() || me.email],
          )
        : t(
            "SESSION_SWITCHED",
            "Votre session a changé dans un autre onglet : un autre compte est maintenant connecté. Ce qui n'avait pas été enregistré ici a été abandonné.",
          )}{" "}
      <button
        type="button"
        className="signout"
        onClick={() => setSwitchedAccount(false)}
      >
        {t("CLOSE", "Fermer")}
      </button>
    </p>
  ) : null;

  /**
   * ═══ UN JETON QUE LE SERVEUR REFUSE N'EST PAS UNE SESSION ═══
   *
   * ⚠️ « CONNECTÉ » NE VOULAIT DIRE QUE « IL Y A UNE CHAÎNE DANS LE STOCKAGE ». Périmé,
   * révoqué, recopié de travers : le jeton passait quand même la garde, et le visiteur
   * atteignait l'accueil — où chacune de ses requêtes échouait en 401 SANS UN MOT. Il
   * voyait un site vide de ses données, sans comprendre, et sans que rien ne lui propose
   * de se reconnecter. C'est le seul endroit où le mensonge se défait : le serveur vient
   * de dire que ce jeton ne vaut rien.
   *
   * ⚠️ ON LE JETTE, on ne se contente pas de l'ignorer. Gardé, il repart dans l'en-tête
   * de chaque requête suivante — y compris après une reconnexion réussie dans un autre
   * onglet, où il écraserait le bon.
   *
   * ⚠️ ET SEULEMENT S'IL Y EN AVAIT UN. Sans jeton, `me` est refusée elle aussi — c'est
   * la situation normale d'un visiteur qui arrive. Purger là n'aurait rien fait, mais
   * aurait annoncé une expiration à quelqu'un qui ne s'est jamais connecté.
   */
  /**
   * ⚠️ ET C'EST BIEN UNE EXPIRATION QU'ON A CONSTATÉE, pas un simple refus. `me` est
   * refusée aussi quand personne n'est connecté — c'est le cas de tout visiteur qui
   * arrive — et annoncer « votre session a expiré » à quelqu'un qui n'en a jamais eu est
   * la petite fausse note qui fait douter du reste. Cet état ne devient vrai QUE si l'on
   * a réellement jeté un jeton, donc il ne se déduit pas au rendu : après la purge,
   * `isSignedIn()` est déjà faux et l'information serait perdue.
   */
  const [sessionExpiree, setSessionExpiree] = useState(false);
  useEffect(() => {
    if (!sessionRefused || !isSignedIn()) return;
    signOut();

    setSessionExpiree(true);
  }, [sessionRefused]);

  /**
   * ═══ OÙ VA QUELQU'UN QU'ON REFUSE ═══
   *
   * ⚠️ SUR L'ACCUEIL PUBLIC QUAND IL Y EN A UN, PLUS SUR LE FORMULAIRE DE CONNEXION
   * (demandé le 2026-08-20). Un site qui s'ouvre au public et qui accueille pourtant
   * par une demande de mot de passe ne s'ouvre pas vraiment : le visiteur ne sait même
   * pas ce qu'il y a derrière. L'accueil, lui, le lui montre, et la barre porte le
   * bouton pour entrer.
   *
   * ⚠️ SAUF SI L'ACCUEIL EST FERMÉ, et ce n'est pas une précaution de style : envoyer
   * vers une page elle-même gardée renvoie depuis cette page, et ainsi de suite.
   *
   * ⚠️ SAUF UNE SESSION QUI VIENT D'EXPIRER. Là il y avait quelqu'un, il sait ce qu'il
   * venait faire, et l'écran de connexion est le seul endroit qui le lui DIT (« votre
   * session a expiré »). Le poser sur l'accueil, c'est le laisser croire qu'il s'est
   * déconnecté tout seul.
   */
  const homeIsOpen = isPublicScreen("home", undefined, value);
  const refusedTo = homeIsOpen && !sessionExpiree ? "/" : "/login";

  /**
   * ⚠️ ET L'ADRESSE VOULUE SURVIT AU DÉTOUR. Renvoyé sur l'accueil, `rawPath` vaut
   * désormais celui de l'accueil : sans ce relais, se connecter depuis la barre
   * ramenait à l'accueil et non à la page qu'on avait demandée. La garde la dépose
   * dans l'état de navigation, le bouton la reprend.
   */
  const intended =
    typeof (navState as { from?: unknown } | null)?.from === "string"
      ? (navState as { from: string }).from
      : rawPath + rawSearch;

  /**
   * CE QU'ON AFFICHE DE LA PERSONNE CONNECTÉE — `undefined` tant qu'on ne le sait pas.
   *
   * ⚠️ LES INITIALES SE PRENNENT SUR LE PREMIER CARACTÈRE *ÉTENDU*, pas sur `[0]` : une
   * initiale accentuée ou un emoji se couperait en deux et sortirait en losange noir.
   * C'est la même précaution que `jourLong` pour sa majuscule.
   *
   * ⚠️ ET UN COMPTE SANS NOM NE DONNE RIEN PLUTÔT QU'UNE PASTILLE VIDE. Le produit en a
   * — un badge créé à la volée, un import incomplet : mieux vaut ne rien montrer qu'un
   * rond gris muet à côté du bouton de déconnexion.
   */
  const identity = (() => {
    const firstName = (me?.firstName ?? "").trim();
    const name = (me?.lastName ?? "").trim();
    if (!firstName && !name) return undefined;
    const letter = (word: string) => [...word][0] ?? "";
    return {
      name: [firstName, name].filter(Boolean).join(" "),
      initials: `${letter(firstName)}${letter(name)}`.toLocaleUpperCase(),
    };
  })();

  /**
   * LE MENU DU COMPTE — ouvert au clic, fermé partout ailleurs.
   *
   * ⚠️ IL SE FERME AU CLIC EXTÉRIEUR *ET* À `Échap`. Un menu qui ne se ferme qu'en
   * recliquant sur son propre bouton reste ouvert par-dessus la page dès qu'on regarde
   * ailleurs — et au clavier, il devient un cul-de-sac.
   *
   * ⚠️ `mousedown` ET NON `click` : un clic dont le bouton s'enfonce dehors et se relâche
   * dedans ne doit pas rouvrir ce qu'il vient de fermer.
   */
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  /**
   * LA PHOTO DE LA PERSONNE, quand le produit en a une — voir `auth/photo.ts`, qui dit
   * laquelle et pourquoi ce n'est pas une photo de profil au sens habituel.
   */
  const photo = usePhoto(me?.id);
  const [photoBroken, setPhotoBroken] = useState(false);
  useEffect(() => {
    if (!menuOpen) return;
    const dehors = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", dehors);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", dehors);
      document.removeEventListener("keydown", escape);
    };
  }, [menuOpen]);

  /**
   * ═══ LA TYPOLOGIE DU PARTICIPANT DÉCIDE DE CE QU'IL REÇOIT ═══
   *
   * ⚠️ ELLE NE LE FAISAIT PAS, ET C'EST TOUT LE SUJET DE LA CASCADE QUI PASSAIT À CÔTÉ.
   * La portée de lecture ne connaissait que le sélecteur « en tant que » de la barre :
   * un participant recevait donc la configuration COMMUNE quelle que soit sa réponse, et
   * un module coupé pour les visiteurs — la société, par exemple — leur restait servi.
   * Choisir son profil ne changeait rien à ce qu'on lui demandait ensuite.
   *
   * ⚠️ ET C'EST LE SHELL QUI LA POSE, parce qu'il est le seul à connaître à la fois le
   * MODE et les RÉPONSES : le fournisseur des réglages vit au-dessus de lui et n'a ni
   * l'un ni l'autre. La règle elle-même est ailleurs, et testée — voir
   * `funnel/viewerTypo.ts`, qui raconte les deux façons de confondre les deux portées.
   *
   * ⚠️ TOUT EN DÉCOULE, sans qu'un seul écran ait à s'en occuper : le fil se recompose
   * (il lit `value`), les textes changent, les couleurs aussi. C'est la raison d'être
   * d'une cascade — la poser au bon endroit suffit.
   */
  const { setViewerTypo } = useSettings();
  const readTypo = viewerTypoOf(mode, answers, recorded);
  useEffect(() => {
    setViewerTypo(readTypo);
  }, [readTypo, setViewerTypo]);

  /* Ce qui recompose le fil — la configuration, ce que l'événement porte, et la réponse
     du participant. Lu une fois ici, comme dans les neuf autres endroits qui calculent
     le fil : voir `funnel/useOffre.ts`. */
  const offer = useOffer();

  /**
   * ON ENTRE DANS LE TUNNEL — par opposition à y circuler.
   *
   * ⚠️ CE N'EST PAS « COMMENT LE DOCUMENT EST ARRIVÉ », mais « D'OÙ VIENT-ON ». Sortir
   * vers l'accueil puis revenir par le menu « Inscription » est une entrée aussi sûrement
   * qu'un lien d'e-mail — tout y est interne, et pourtant on entre. Trois critères sont
   * morts avant celui-ci : voir `funnel/entree.ts`, qui les raconte.
   *
   * ⚠️ ET UN F5 N'EN EST PAS UNE : il rouvre l'entrée d'historique où l'on était, avec
   * ses réponses, et l'on y reste (tâche #503, voir `isResumed`). Sans quoi les deux
   * gardes ci-dessous renvoyaient au début qui rechargeait en pleine inscription, et à sa
   * confirmation qui rechargeait en pleine correction.
   */
  const arrival = isEntry(
    pathname,
    usePrevious(pathname),
    isResumed(pathname, documentRestored(), answers),
  );

  const enteredMidway =
    inFunnel &&
    !participationEnCours &&
    arrival &&
    recorded?.responded !== true;

  /**
   * ═══ RIEN À CONFIRMER : LA CONFIRMATION RENVOIE AU DÉBUT, D'OÙ QU'ON VIENNE ═══
   * (« Mon inscription » d'un non-inscrit, 2026-10-27)
   *
   * ⚠️ LA GARDE D'ENTRÉE NE VOYAIT PAS CE CAS. Le menu du compte mène à la confirmation
   * SANS réponses. Ouvert depuis une étape du tunnel, ce n'est pas une entrée (on venait
   * d'une étape), donc personne ici ne redirigeait : il ne restait que le renvoi de la
   * confirmation elle-même, et il perdait la course. Son `<Navigate>` part, puis la note de
   * `loin` ci-dessous rejoue l'adresse de la confirmation (`replace`) dans le même passage
   * des effets, et c'est elle qui l'emporte. Mesuré sur la pile locale : écran vide sous le
   * fil d'Ariane, et `loin` posé au rang de la confirmation.
   *
   * ⚠️ ET CE `loin` OUVRAIT LE FIL D'ARIANE JUSQU'À LA CONFIRMATION. Participation, Dates,
   * puis Confirmation dans le fil : l'écran écrivait une inscription complète, sans
   * typologie, sans billetterie (donc sans rien à payer), sans informations. Mesuré aussi.
   *
   * La règle est celle de `lost` dans `routes/Confirmation.tsx` : ni réponse dans ce
   * parcours, ni réponse enregistrée. Posée ICI, la redirection part avant que l'écran
   * soit rendu, et `redirect` coupe les deux écritures de l'état qui suivent (le rang
   * `loin`, la réponse de l'inscription automatique). Le renvoi de la confirmation reste
   * en filet. ⚠️ SEULEMENT UNE FOIS LA PARTICIPATION CONNUE, comme la garde d'entrée : un
   * inscrit dont la lecture est en vol n'est pas un non-inscrit.
   */
  const nothingToConfirm =
    pathname === CONFIRMATION &&
    knownParticipation &&
    !participationEnCours &&
    !hasAnswered(answers) &&
    recorded?.responded !== true;

  /**
   * ═══ DÉJÀ INSCRIT, ARRIVÉ SUR LE TUNNEL → SA CONFIRMATION ═══
   *
   * L'exact symétrique de la garde ci-dessus, et le cas le plus courant des deux : le
   * lien d'invitation part à TOUT LE MONDE, y compris à ceux qui ont déjà répondu. Cliqué
   * par eux, il ouvrait l'étape 1 — « Comment participez-vous ? » — à quelqu'un qui a déjà
   * tout rempli. On leur redemandait ce qu'ils venaient de donner, et rien à l'écran ne
   * disait qu'ils étaient inscrits.
   *
   *   pas répondu → la première étape, on repart du début
   *   déjà répondu → la confirmation, on voit ce qui est enregistré
   *
   * ⚠️ À L'ENTRÉE, JAMAIS EN CIRCULANT — et c'est ce qui rend la règle tenable.
   * « Modifier mon inscription » mène au tunnel exprès, le fil d'Ariane rouvre l'étape
   * qu'on veut corriger : les rediriger serait fermer la porte qu'ils ouvrent. Ce qui les
   * distingue, c'est qu'on VENAIT déjà d'une étape.
   *
   * ⚠️ ET PAS DEPUIS LA CONFIRMATION ELLE-MÊME : elle est du tunnel, donc sans cette
   * réserve on se redirigerait vers l'endroit où l'on est déjà.
   *
   * ⚠️ NI QUAND UN REFUS ARRIVE RÉPONSE EN MAIN POUR VENIR (bug #522). « Ma
   * participation » l'envoie à l'étape qui suit la participation, avec « sur place » ou
   * « à distance » dans l'état : il n'a pas d'inscription à revoir, il en fait une. Le
   * renvoyer à la confirmation lui remontrerait son refus.
   */
  const rejoining = !isRegistered(recorded) && comes(answers.participation);
  const alreadyRegistered =
    inFunnel &&
    knownParticipation &&
    arrival &&
    recorded?.responded === true &&
    !rejoining &&
    pathname !== CONFIRMATION;

  /**
   * ═══ OÙ L'ON ENVOIE, ET POURQUOI ═══ calculé ICI, avant tout le reste, parce que deux
   * choses en dépendent : le rendu (plus bas) et le fait de NE PAS noter son avancement
   * sur une étape qu'on quitte (juste en dessous).
   */
  const toConfirmation = mode === "visitor" && alreadyRegistered;
  const toStart =
    mode === "visitor" &&
    inFunnel &&
    (!inThread(value, pathname, offer) || enteredMidway || nothingToConfirm);
  const redirect = toConfirmation || toStart;

  /**
   * JUSQU'OÙ EST-ON ALLÉ — noté ICI, une fois, plutôt que par chaque étape.
   *
   * ⚠️ ET C'EST LA RAISON D'ÊTRE DE CETTE LIGNE : la version d'avant faisait marquer son
   * passage à chaque écran, huit fois le même geste, et l'étape ajoutée sans y penser ne
   * marquait rien — donc jamais cochée, jamais rouvrable. C'est arrivé à la billetterie.
   * Le rang se lit sur le fil calculé, à l'arrivée, quel que soit l'écran.
   *
   * ⚠️ IL NE SERT QU'AU FIL D'ARIANE. Aucune garde ne le consulte : voir `loin` dans
   * `funnel/answers.ts`.
   *
   * ⚠️ ET SURTOUT PAS SUR UNE ÉTAPE QU'ON QUITTE À L'INSTANT — c'est ce qui manquait, et
   * ça CASSAIT LES GARDES D'ARRIVÉE, celle-ci comprise. `memorise` NAVIGUE (`replace`),
   * donc il change `location.key` : le marqueur « le document vient d'arriver ici »
   * disparaissait avant même que les redirections aient statué. Mesuré : arriver
   * directement sur `/registration/informations` sans avoir répondu n'y redirigeait plus
   * personne, alors que la garde qui l'interdit est écrite juste au-dessus.
   *
   * Noter son avancement sur un écran qu'on ne verra pas n'avait de toute façon aucun
   * sens : on attend que les gardes aient laissé passer.
   */
  const rank = activeSteps(value, offer).findIndex((s) => s.path === pathname);
  useEffect(() => {
    if (redirect) return;
    if (rank >= 0 && rank > (answers.loin ?? -1)) remembered({ loin: rank });
    // `answers` et `memorise` se refont à chaque rendu ; ce qui déclenche, c'est le
    // changement d'étape.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rank, redirect]);

  /**
   * ═══ UN PARCOURS QUI NE POSE PLUS LA QUESTION RÉPOND À LA PLACE ═══
   *
   * ⚠️ WITHOUT THIS, THE SETTING DID THE OPPOSITE OF WHAT IT PROMISES. It takes the
   * question out of the funnel; nobody answered any more, and the final write falls back
   * to "not coming" (see `saveParticipation`, which reads `answers.participation ?? what
   * the server holds`). An event meaning to register everybody registered nobody — and
   * nothing on screen said so.
   *
   * ⚠️ ET CE N'EST PAS PROPRE À L'INSCRIPTION AUTOMATIQUE : l'interrupteur « Servie » de
   * l'étape la retire aussi, et ce cas-là n'était pas couvert — trois étapes remplies, et
   * « Merci de nous avoir prévenus » à l'arrivée, jours supprimés. La question se pose
   * donc au fil calculé (`answersForParticipant`), pas à un réglage.
   *
   * ⚠️ VISITORS ONLY. An organiser walks their own screens to configure them; putting an
   * answer into their navigation state would register them to the event they are
   * preparing as soon as they opened the confirmation.
   *
   * ⚠️ AND ONLY FOR SOMEBODY WHO HAS ANSWERED NOTHING. The setting registers those who
   * are not asked; it has no business overturning an answer already given — somebody the
   * back office has removed would find themselves registered for having reopened the
   * funnel.
   *
   * ⚠️ AFTER THE REDIRECTS, like the rank above: `memorise` navigates with `replace`, and
   * writing while a guard is still deciding amounts to deciding in its place.
   */
  const answersItself = answersForParticipant(value, offer);
  useEffect(() => {
    if (redirect || mode !== "visitor" || !inFunnel || !answersItself) return;
    if (answers.participation !== undefined) return;
    /**
     * ⚠️ ET SEULEMENT UNE FOIS LA PARTICIPATION CONNUE. `recorded` vaut `undefined` tant
     * que la requête est en vol, ou si elle a échoué. Le lire comme « n'a pas répondu »
     * reposait `onsite` sur quelqu'un qui a décliné, et la confirmation l'écrivait à
     * chaque ouverture : c'est la course qui réintroduisait le bug d'écriture-à-chaque-
     * visite du 2026-08-03. On attend de SAVOIR au lieu de supposer.
     */
    if (!recorded || recorded.responded === true) return;
    remembered({ participation: "onsite" });
    // What triggers it: entering the funnel of an event with automatic registration.
    // `answers` and `memorise` are rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answersItself, inFunnel, mode, redirect, recorded?.responded]);
  const wouldLose =
    inFunnel &&
    answers.participation !== undefined &&
    recorded?.responded !== true;

  /**
   * ═══ ON N'ENTRE PAS DANS LE TUNNEL PAR LE MILIEU ═══
   *
   * Ouvrir `/registration/informations` dans un onglet neuf servait le formulaire à
   * quelqu'un dont on ignore encore s'il vient — et la confirmation, atteinte ainsi,
   * écrivait une inscription sans réponse de participation. Un lien d'e-mail périmé, un
   * signet, une adresse recopiée : trois façons ordinaires d'arriver là.
   *
   * Deux conditions, et il faut les DEUX :
   *
   *   — ON ENTRE DANS LE TUNNEL, on n'y circule pas. Un lien d'e-mail, un F5, ou le
   *     menu « Inscription » depuis l'accueil : dans les trois cas on n'était pas sur une
   *     étape juste avant. On bloque une entrée, jamais un pas de plus dans un parcours
   *     en cours. ⚠️ TROIS CRITÈRES SONT MORTS AVANT CELUI-CI, chacun en laissant la
   *     garde inopérante sans un mot — voir `funnel/entree.ts`.
   *
   *   — PAS ENCORE RÉPONDU. `responded` est la colonne que le back-office montre sous
   *     « Réponse » : vraie, le parcours est ouvert en entier et l'on rouvre l'étape que
   *     l'on veut pour la corriger ; fausse, on repart du début. C'est la même bascule
   *     que le fil d'Ariane (voir `funnel/Steps.tsx`), et il n'y en a pas d'autre.
   *
   * ⚠️ ET SEULEMENT UNE FOIS LA RÉPONSE DU SERVEUR CONNUE. Redirigé pendant que la
   * question est en vol, un inscrit se serait fait renvoyer au début de son propre
   * parcours — la requête arrive après le premier rendu, et `recorded` y vaut
   * `undefined` sans que cela signifie « pas inscrit ».
   */
  const leave = () => {
    setAsking(false);
    navigate(withContext("/", context));
  };
  const goHome = () => (wouldLose ? setAsking(true) : leave());
  /* Le réglage s'il est écrit, sinon le nom de l'événement — et l'onglet le reprend.
     Voir `settings/eventName.ts`. */
  const eventName = useEventName();
  useDocumentTitle();
  const monogram = useText(
    "FO_MEETING_V2_EVENT_LOGO_TEXT",
    fallbackOf("FO_MEETING_V2_EVENT_LOGO_TEXT"),
  );
  /**
   * ⚠️ LA PORTÉE D'ÉCRITURE, LUE ICI AUSSI. Le châssis en fait un bandeau qui ne
   * défile pas ; le panneau en fait une explication. Les deux lisent `scopeKindOf`,
   * jamais leur propre version de la règle.
   */
  const overScope = scopeKindOf(scope);
  const languageLabel =
    languages.find((l) => l.id === scope.languageId)?.name ?? "";
  const typoName = typos.find((t) => t.id === scope.typoId)?.name ?? "";

  const signOutLabel = useText(
    "FO_MEETING_V2_SIGNOUT_LABEL",
    fallbackOf("FO_MEETING_V2_SIGNOUT_LABEL"),
  );
  /* Le mot de l'écran de connexion, réemployé sur le bouton qui y mène : c'est la
     même porte, elle ne se nomme pas de deux façons. */
  const signInLabel = useText(
    "FO_MEETING_V2_LOGIN_SUBMIT",
    fallbackOf("FO_MEETING_V2_LOGIN_SUBMIT"),
  );
  /* Connexion fermée par l'organisateur (« Mode de connexion » : Désactivée). */
  const signInClosed = value("FO_MEETING_V2_LOGIN_DISABLED") === true;

  /**
   * L'ICÔNE DE L'ONGLET — un réglage comme un autre, appliqué là où il se voit.
   *
   * ⚠️ ELLE PASSE PAR `fileUrl`, COMME LES DEUX BANDEAUX. Cette ligne a longtemps
   * refusé les clés nues plutôt que de les résoudre, au motif que le faire aurait
   * demandé de coder le seau en dur. Le raisonnement était mauvais : le seau public
   * n'a rien d'une adresse d'environnement, la V1 le nomme en clair dans six de ses
   * écrans et sert le même en dev comme en production. Surtout, la prudence n'a été
   * appliquée qu'ICI : l'image d'en-tête et celle du pied avaient exactement le même
   * problème et s'affichaient cassées, sans un mot. Une garde qui ne protège qu'un
   * cas sur trois ne protège rien.
   */
  const faviconUrl = fileUrl(useText("FO_MEETING_V2_FAVICON"));

  /**
   * HOVER A BLOCK, ITS SETTINGS OPEN BESIDE IT.
   *
   * Hover-opened panels that hold fields are notoriously fragile: you reach for a
   * field, the pointer crosses the gap, the panel goes out from under your hand.
   * Four guards, and it holds:
   *
   *   1. AN INTENT DELAY before opening. Sweeping across the page must not set
   *      cards flashing. Once one is open, switching to another block is
   *      immediate — the intent is no longer in doubt.
   *   2. A GRACE PERIOD before closing, ample enough to cross the ten pixels
   *      between a block and its card.
   *   3. THE CARD IS ITSELF A HOVER TARGET: entering it cancels the closing. This
   *      is what makes the gap crossable rather than fatal.
   *   4. WHILE A FIELD HAS FOCUS THE CARD FOLLOWS THE KEYBOARD, NOT THE MOUSE.
   *      It neither closes nor switches: a hand left on the mouse cannot take the
   *      field away mid-word. Clicking anywhere else blurs it and the pointer
   *      takes over again.
   *
   * Delegated here rather than wired onto each element: a screen landing in phase
   * 2 becomes configurable simply by carrying `data-cfg`. Elements without it are
   * untouched — the navigation has none precisely so it stays navigable, and
   * every control on the page keeps its own clicks.
   */
  const cancelTimers = () => {
    window.clearTimeout(openIn.current);
    window.clearTimeout(closeIn.current);
  };

  const release = () => {
    cancelTimers();
    closeIn.current = window.setTimeout(() => {
      if (document.activeElement?.closest?.(".spot")) return;
      setSpot(null);
    }, CLOSE_DELAY);
  };

  const aim = (e: React.MouseEvent) => {
    if (mode !== "organiser") return;
    const block = (e.target as HTMLElement).closest<HTMLElement>("[data-cfg]");
    if (!block) return release();
    cancelTimers();
    if (block === spot) return;

    // THE CARD FOLLOWS THE KEYBOARD, NOT THE MOUSE. While one of its fields has
    // focus it does not move at all: brushing past the footer used to replace the
    // very field being typed into. Clicking anywhere else blurs the field — the
    // browser does that on its own — and the pointer takes over again.
    if (document.activeElement?.closest?.(".spot")) return;

    openIn.current = window.setTimeout(
      () => setSpot(block),
      spot ? 0 : OPEN_DELAY,
    );
  };

  const leaveOrganiserMode = () => {
    // Leaving edit mode must not leave anything behind: an element still marked
    // as "pointed at" would show a brand outline to a visitor.
    document
      .querySelectorAll(".aimed")
      .forEach((el) => el.classList.remove("aimed"));
    cancelTimers();
    setSpot(null);
    setRecap(false);
    setMode("visitor");
  };

  /**
   * A link that names an event we cannot resolve gets an ANSWER, not a spinner
   * that never ends. Rendered inside the shell so it still looks like the site
   * rather than a browser error — but with the seeded identity, because we do not
   * know whose event it was.
   */
  if (
    event.kind === "missing" ||
    event.kind === "unknown" ||
    event.kind === "unavailable"
  ) {
    return (
      <div className="shell" data-mode="visitor" data-view="desktop">
        <div className="wrap">
          <div className="stagearea">
            <div className="site">
              <section className="page">
                <div className="ttl">
                  {/* ⚠️ CES TROIS-LÀ PARLENT SOUVENT FRANÇAIS, ET C'EST LA LIMITE :
                      le dictionnaire arrive avec l'événement, et il n'y en a pas.
                      Branchées quand même — le domaine, lui, peut être connu. */}
                  <h2>
                    {event.kind === "missing"
                      ? t(
                          "EVENT_MISSING_FROM_URL",
                          "Il manque l’événement dans l’adresse",
                        )
                      : event.kind === "unknown"
                        ? t(
                            "LINK_LEADS_NOWHERE",
                            "Ce lien ne mène à aucun événement",
                          )
                        : t(
                            "EVENT_UNREACHABLE",
                            "L’événement est momentanément injoignable",
                          )}
                  </h2>
                  <p>
                    {event.kind === "missing"
                      ? t(
                          "EVENT_MISSING_FROM_URL_DETAIL",
                          "Cette adresse ne désigne aucun événement. Elle a sans doute été tronquée en la recopiant. Le lien reçu par email ou le QR code de votre invitation vous y mènera.",
                        )
                      : event.kind === "unknown"
                        ? t(
                            "LINK_LEADS_NOWHERE_DETAIL",
                            "Il a peut-être été fermé, ou l’adresse a été tronquée en la recopiant. Le lien reçu par email reste le bon point de départ.",
                          )
                        : t(
                            "EVENT_UNREACHABLE_DETAIL",
                            "Réessayez dans un instant. Si cela persiste, l’organisateur de l’événement pourra vous renseigner.",
                          )}
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * NOTHING OF THE SITE BEFORE IT IS THE RIGHT ONE.
   *
   * Until the event's configuration is in hand, the rows are the SEED: a visitor
   * saw demonstration colours and demonstration wording for a moment, then the
   * event's own. That is worse than a wait — it looks like somebody else's site,
   * and on a slow connection there is time to read it.
   *
   * ⚠️ BEFORE the sign-in guard, and that order matters: redirecting to sign-in
   * while the event is still loading would drop whatever address the visitor
   * followed, so a slow connection would silently lose their destination.
   *
   * The holding screen is deliberately NEUTRAL. It cannot wear the event's palette
   * — that palette is precisely what is being fetched — so it wears none rather
   * than guessing one and flashing twice.
   *
   * ═══ ET SA PARTICIPATION EN FAIT PARTIE, PARCE QU'ELLE PORTE SA TYPOLOGIE ═══
   *
   * ⚠️ CE N'EST PAS UN SOUCI DE SCINTILLEMENT, C'EST UNE FUITE. La typologie décide de
   * la configuration servie — jusqu'aux ÉTAPES du parcours (voir `funnel/viewerTypo.ts`).
   * Peint avant qu'elle soit connue, le site montre le niveau COMMUN : un instant, on
   * affiche à quelqu'un les modules, les tuiles et les textes que sa typologie n'a
   * précisément pas le droit de voir. Sur une connexion lente, il y a le temps de lire.
   *
   * ⚠️ SEULEMENT S'IL Y A UNE SESSION, et c'est ce qui garde l'attente honnête. Sans
   * jeton il n'y a ni typologie ni inscription à connaître : les deux requêtes ne
   * feraient qu'échouer, et l'écran de connexion — le premier que voit un visiteur qui
   * arrive — attendrait deux allers-retours pour rien.
   *
   * ⚠️ RÉGLÉE, PAS RÉUSSIE — la même règle que `ready` juste au-dessus. Et surtout, ce
   * n.est PAS l.inverse de « en cours » : `loading` repart à chaque relance de requête, si
   * bien que la garde démontait le site, que le remontage relançait la requête, et ainsi
   * de suite — 1 819 appels en 6 secondes, mesurés. Voir `settled` dans
   * `funnel/useParticipation.ts`, qui ne revient jamais en arrière.
   */
  if (event.kind === "loading" || !ready || (signedIn && !knownParticipation)) {
    return (
      <div className="shell" data-mode="visitor" data-view="desktop">
        <div className="wrap">
          <div className="stagearea">
            <div className="boot">
              <span className="spin" aria-hidden="true" />
              {/* Announced, not merely drawn: a spinner tells a screen reader
                  nothing at all. */}
              <span className="a11y" role="status">
                {t("LOADING_EVENT", "Chargement de l'événement")}
              </span>
              {switchedNotice}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * ⚠️ AUCUNE ÉTAPE DU TUNNEL N'EST UN CUL-DE-SAC.
   *
   * The settings can take a step out of the funnel — automatic registration removes
   * the participation question, a closed registration keeps only the first screen.
   * A participant reaching such a step by a bookmark, an old email link or the Back
   * button used to land on a screen with nothing to answer, no breadcrumb, and a
   * disabled button. Measured after switching automatic registration on: an empty
   * rail and a dead "Continuer". They go to the first step that IS in the funnel.
   *
   * ⚠️ VISITORS ONLY, and for the same reason as the confirmation's own guard: the
   * organiser opens exactly these screens, on purpose, to configure them.
   *
   * ⚠️ TROIS MOTIFS, UNE SEULE REDIRECTION — et DEUX destinations. Séparés, ils auraient
   * rendu plusieurs `<Navigate>` depuis le même rendu :
   *
   *   déjà inscrit, on ARRIVE     → sa confirmation (voir `dejaInscrit`)
   *   l'étape n'est plus du fil   → la première étape servie
   *   entrée par le milieu        → idem (voir `entreeParLeMilieu`)
   *
   * ⚠️ LE PREMIER PASSE DEVANT, et il le faut : quelqu'un d'inscrit qui ouvre son vieux
   * lien sur une étape depuis retirée du parcours relève des DEUX règles. Le renvoyer au
   * début lui redemanderait ce qu'il a déjà donné, alors que sa réponse existe.
   */
  /**
   * ═══ WHAT GOES IN THE CONTENT AREA: THE SCREEN, OR THE REDIRECT THAT REPLACES IT ═══
   *
   * ⚠️ A REDIRECT IS RENDERED *INSIDE* THE CHROME, NEVER INSTEAD OF IT. `<Navigate>`
   * draws nothing, so returning it from here used to blank the WHOLE site — bar, banner,
   * navigation, footer — for as long as the new address took to settle. Measured going
   * from the home screen to "Inscription": 96 ms with nothing on screen at all, not even
   * the loading spinner, before everything came back. That is the flicker; the boot
   * screen was never involved.
   *
   * Nothing else changes: the redirect fires from the same render, on the same
   * conditions, with the same `replace`. Only the frames in between now keep the site
   * around them.
   */
  const target = redirect
    ? toConfirmation
      ? CONFIRMATION
      : firstStep(value, offer)?.path
    : undefined;

  /**
   * ═══ UN COMPTE QU'ON N'A PAS PU LIRE N'EST PAS UN COMPTE NON INSCRIT (bug 9) ═══
   *
   * ⚠️ UN 500 SUR « QUI SUIS-JE » (OU SUR LA PARTICIPATION) LAISSAIT `recorded` VIDE, ET TOUT
   * LE SITE LISAIT CE VIDE COMME « PAS INSCRIT » : l'accueil proposait de s'inscrire à
   * quelqu'un d'inscrit, et le tunnel repartait de zéro. Une panne ne se lit jamais comme
   * une réponse négative. Tant que l'identité n'est pas lue, le contenu cède la place à un
   * écran qui le dit et propose de réessayer. Le 401 en est exclu : c'est une session morte,
   * traitée plus haut (`sessionRefused`).
   *
   * ⚠️ AVANT LES REDIRECTIONS, qui se calculent sur cette même participation absente.
   */
  const identityUnavailable =
    signedIn && identityUnreadable && !sessionRefused;

  const content = identityUnavailable ? (
    <section className="page" role="alert">
      <div className="ttl">
        <h2>
          {t("IDENTITY_UNAVAILABLE", "Votre compte n'a pas pu être lu")}
        </h2>
        <p>
          {t(
            "IDENTITY_UNAVAILABLE_DETAIL",
            "Le serveur n'a pas répondu comme il le devrait. Votre inscription n'est pas perdue : réessayez dans un instant.",
          )}
        </p>
      </div>
      <Button
        type="button"
        loading={identityRetrying}
        onClick={() => retryIdentity()}
      >
        {t("RETRY", "Réessayer")}
      </Button>
    </section>
  ) : target && target !== pathname ? (
      <Navigate to={withContext(target, context)} replace />
    ) : closed ? (
      /* ⚠️ AVANT LA GARDE DE SESSION : renvoyer quelqu'un se connecter pour ne rien
         trouver derrière, c'est lui faire payer une porte qui ne mène nulle part. Rendue
         sur place, sans redirection : l'adresse reste celle qu'il a suivie. */
      <NotFound />
    ) : locked ? (
      // `replace`: the guarded address must not sit in the history, or Back walks
      // straight into the redirect again.
      <Navigate
        to={withContext(refusedTo, context)}
        replace
        /* And we say WHY: sent back to sign-in with no explanation, people think they
           misclicked. See `routes/Login.tsx`. Vers l'accueil public, `from` sert la même
           chose d'un cran plus loin : c'est le bouton de la barre qui la portera. */
        state={{ from: rawPath + rawSearch, expiree: sessionExpiree }}
      />
    ) : (
      <Outlet />
    );

  return (
    /* The mode reaches the SCREENS, not just the shell: one of them has to know
       whether it is being read by a participant or previewed by an organiser —
       see `panel/mode.ts`. */
    <ModeProvider value={mode}>
      <OrganiserViews
        anonymous={asAnonymous}
        situation={mode === "organiser" ? situation : null}
        setSituation={setSituation}
      >
        {/* ⚠️ HORS DU SITE, ET C'EST LE SEUL RÉGLAGE DANS CE CAS. React 19 remonte ce
        `<link>` dans le `<head>` d'où qu'il soit rendu, ce que l'`index.html` annonce
        déjà : l'identité de l'événement se pose depuis les composants. Un onglet
        appartient au DOCUMENT, pas au cadre de prévisualisation — les couleurs, elles,
        sont peintes sur `.site` et jamais sur l'outil autour (voir `useTheme`). En
        mode organisateur c'est ce qu'on veut : l'icône se vérifie là où elle
        s'affiche, faute de quoi elle ne se vérifierait nulle part. */}
        {faviconUrl !== "" && <link rel="icon" href={faviconUrl} />}
        <div
          className="shell"
          // Now that the attribute values and the state share one vocabulary, the
          // translation between them is gone — and with it the chance of the two
          // drifting apart.
          data-mode={mode}
          data-view={view}
        >
          {mode === "organiser" && (
            <OrganiserBar
              view={view}
              onView={setView}
              asAnonymous={asAnonymous}
              onAsAnonymous={setAsAnonymous}
              onRecap={() => setRecap(true)}
              onLeave={leaveOrganiserMode}
            />
          )}
          {mode === "organiser" && overScope !== "reference" && (
            /**
             * ═══ LE BANDEAU DE SURCHARGE, EN TRAVERS DE L'ÉDITEUR ═══
             *
             * ⚠️ SOUS LA BARRE ET NON DANS LE PANNEAU, et c'est tout l'intérêt. Le
             * panneau le disait déjà, mais dans une boîte qui défile avec les réglages :
             * trois familles plus bas, on ne sait plus dans quelle langue on écrit. Ici
             * il ne bouge pas, il traverse le site ET le panneau, et il est dans l'œil de
             * quiconque regarde son écran — le panneau, lui, garde l'explication.
             *
             * ⚠️ IL NE S'AFFICHE QUE QUAND IL A QUELQUE CHOSE À DIRE. Posé aussi sur la
             * référence, il aurait pris vingt pixels de hauteur en permanence pour
             * annoncer l'état normal, et on aurait cessé de le voir — donc de le lire le
             * jour où il change.
             */
            <div className="obar__over" role="status">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3 3 7.5l9 4.5 9-4.5z" />
                <path d="M3 14.5 12 19l9-4.5" />
              </svg>
              <b>Surcharge</b>
              <span className="obar__overw">
                {overScope === "typo"
                  ? `${typoName} · ${languageLabel} — ce que vous réglez ne vaut que pour cette typologie`
                  : `${languageLabel} — ce que vous réglez ne vaut que pour cette langue`}
              </span>
            </div>
          )}

          <div className="wrap">
            {/* The WORK AREA centres the site in the space the panel leaves. Without
            it, the mobile preview stuck the 390px frame against the left edge with
            dead space beside it. */}
            <div className="stagearea">
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div
                ref={setSite}
                className="site"
                onMouseOver={aim}
                onMouseLeave={release}
              >
                {/* La feuille propre à l'événement, DANS le site : elle disparaît avec
                lui, et chacune de ses règles y est confinée. Voir `CustomCss`. */}
                <CustomCss />
                {!bare && (
                  <header className="appbar">
                    {/* ⚠️ THE BRAND LEADS HOME. Inside the funnel there was NO WAY OUT
                  at all — no navigation yet, and every button moves forward. Somebody
                  who changed their mind was left with the browser's Back button,
                  which walks them backwards through their own answers one screen at
                  a time. The event's name is where everyone looks for the way home,
                  so that is where it is. */}
                    <div className="brand" data-cfg="identity">
                      <button
                        type="button"
                        className="brand__go"
                        onClick={goHome}
                      >
                        <span className="logo">{monogram}</span>
                        <span className="evname">{eventName}</span>
                      </button>
                    </div>
                    <SiteNav />

                    {/* The LANGUAGE lives in the site AND in the organiser bar: here it is a
                  real visitor control, unlike the typology (a participant has only
                  one). It is also the second axis of the cascade — so switching it
                  here moves what the panel writes, which is exactly right: you
                  translate the page you are looking at. */}
                    <div className="appbar__end">
                      {/**
                       * QUI EST CONNECTÉ — `.who`, repris du prototype, et devenu la porte du
                       * compte : c'est par lui qu'on se déconnecte, ce que le prototype ne
                       * prévoit pas (il n'a aucune déconnexion).
                       *
                       * ⚠️ IL NE S'AFFICHE QU'EN DESKTOP, et c'est le prototype qui le décide
                       * (`.who { display: none }`, relevé par le bloc `@container`). Sur un
                       * téléphone la barre porte déjà le monogramme, le nom de l'événement, la
                       * déconnexion et la langue : un nom de plus la ferait déborder, et c'est
                       * l'écran où l'on sait déjà à qui appartient l'appareil.
                       *
                       * ⚠️ LE PRÉNOM SEUL À CÔTÉ DES INITIALES, comme le prototype
                       * (« CR · Camille ») : le nom complet répété deux fois n'apprend rien et
                       * pousse la barre. Les initiales viennent des deux noms, elles.
                       *
                       * ⚠️ ET RIEN TANT QU'ON NE SAIT PAS. `me` arrive par une requête : rendu
                       * avec un nom vide, le bloc afficherait une pastille grise sans lettres,
                       * qui sauterait ensuite au chargement.
                       */}
                      {reader && identity && (
                        <div className="who" ref={menuRef}>
                          <button
                            type="button"
                            className="who__b"
                            /**
                             * ⚠️ LE NOM EST PORTÉ PAR LE BOUTON, PAS PAR CE QUI S'AFFICHE
                             * (bug #407). Sous 720 px, `.who__n` est en `display: none` et
                             * la pastille en `aria-hidden` : sans ce libellé, le lecteur
                             * d'écran n'annonçait que « bouton, menu ». Le même nom que le
                             * bureau affiche, sur toutes les tailles.
                             */
                            aria-label={identity.name}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen((o) => !o)}
                          >
                            {/**
                             * ⚠️ LA PHOTO S'IL Y EN A UNE, LES INITIALES SINON — et les
                             * initiales restent le cas le plus courant. Ce que le produit
                             * stocke n'est pas une photo de profil choisie par la personne :
                             * c'est le visage de référence que le back-office attache pour que
                             * la reconnaissance la retrouve dans l'album (voir `auth/photo.ts`).
                             *
                             * ⚠️ ET SI ELLE NE CHARGE PAS, ON REVIENT AUX INITIALES. L'adresse
                             * est SIGNÉE, donc elle expire : gardée telle quelle, une longue
                             * session finirait sur l'icône d'image cassée du navigateur, qui se
                             * lit comme une panne du site.
                             */}
                            <i aria-hidden="true">
                              {photo && !photoBroken ? (
                                <img
                                  src={photo}
                                  alt=""
                                  onError={() => setPhotoBroken(true)}
                                />
                              ) : (
                                identity.initials
                              )}
                            </i>
                            {/* Le nom entier, replié en mobile par le CSS — la pastille, elle,
                          reste : c'est par elle qu'on atteint la déconnexion. */}
                            <span className="who__n">{identity.name}</span>
                            {/* Ce qui dit qu'il y a quelque chose dessous. Sans lui, un nom
                          gris en bout de barre ne se distingue pas d'une étiquette, et
                          personne ne pense à cliquer. */}
                            <span className="who__c" aria-hidden="true">
                              <Icon name="chevron" />
                            </span>
                          </button>

                          {menuOpen && (
                            <div className="who__menu" role="menu">
                              {/* ⚠️ LE NOM ET L'ADRESSE EN TÊTE, et c'est le mobile qui l'exige :
                            là-bas la barre ne montre qu'une pastille de deux lettres, et
                            deux initiales ne répondent pas à « suis-je connecté avec le
                            bon compte ? » — question qui se pose précisément avant de se
                            déconnecter, ou sur un appareil partagé. */}
                              <div className="who__me">
                                <b>{identity.name}</b>
                                {me?.email && <small>{me.email}</small>}
                              </div>

                              {/* ⚠️ « MON INSCRIPTION » ET NON « MON PROFIL », parce que le
                            second n'existe pas — ni dans la V2, ni dans la V1, qui n'a
                            aucun écran de compte. Nommer une porte d'après ce qu'elle
                            n'ouvre pas est précisément la faute que ce projet s'interdit.
                            La confirmation EST le récapitulatif de ce qui est enregistré
                            pour cette personne : c'est le profil que ce produit a. */}
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  setMenuOpen(false);
                                  /* Sans état de navigation : la confirmation n'écrit que si
                               l'on porte des réponses, donc ouvrir son récapitulatif ne
                               réenregistre rien. */
                                  navigate(
                                    withContext(
                                      "/registration/confirmation",
                                      context,
                                    ),
                                  );
                                }}
                              >
                                <Icon name="inscription" />
                                {t("MY_REGISTRATION", "Mon inscription")}
                              </button>
                              {/* Le libellé reste un réglage, et `data-cfg` suit l'élément :
                            le CSS libre écrit contre l'ancien bouton continue de viser
                            la déconnexion, où qu'elle soit passée. */}
                              <button
                                type="button"
                                role="menuitem"
                                data-cfg="signOut"
                                onClick={() => {
                                  setMenuOpen(false);
                                  signOut();
                                }}
                              >
                                <Icon name="sortie" />
                                {signOutLabel}
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/**
                       * ═══ LA PORTE, POUR QUI N'EST PAS CONNECTÉ ═══
                       *
                       * ⚠️ SANS LUI, UN SITE PUBLIC EST UN CUL-DE-SAC. La pastille du compte
                       * ne se dessine que pour quelqu'un de connecté : sur un accueil ouvert,
                       * la barre n'offrait donc RIEN pour entrer, et il fallait deviner
                       * `/login` ou cliquer une page fermée pour s'y faire renvoyer.
                       *
                       * ⚠️ IL DIT LE MOT DE L'ORGANISATEUR, celui de son propre écran de
                       * connexion (`FO_MEETING_V2_LOGIN_SUBMIT`) : deux mots pour une seule
                       * porte, et le second aurait été à traduire une fois de plus.
                       *
                       * ⚠️ PAS EN MODE ORGANISATEUR : là, personne n'est connecté par
                       * construction, et un bouton de connexion au milieu de la
                       * prévisualisation ferait croire que le site en demande une.
                       */}
                      {!reader && !signInClosed && (
                        <button
                          type="button"
                          className="appbar__in"
                          onClick={() =>
                            navigate(withContext("/login", context), {
                              state: { from: intended },
                            })
                          }
                        >
                          {signInLabel}
                        </button>
                      )}

                      {/* ⚠️ LA LANGUE EN DERNIER, ET C'EST UN ÉCART VOULU AVEC LE PROTOTYPE,
                    qui la pose avant le compte. Elle change ce que TOUTE la page dit,
                    le compte ne concerne que la personne : le réglage le plus large se
                    range au bout de la barre, là où l'œil va le chercher. */}
                      {/* ⚠️ UN ENVELOPPEUR POUR PORTER LE CHEVRON. `appearance: none` retire la
                    flèche du système (elle ne suit ni la teinte de l'événement ni le trait
                    des autres icônes), mais un `select` sans flèche du tout ne dit plus
                    qu'il s'ouvre : c'est un mot posé dans une pastille. On rend donc le
                    signe, avec l'icône du produit — la MÊME que la pastille du compte,
                    juste à côté, qui ouvre elle aussi une liste. */}
                      {/* ⚠️ ABSENT QUAND L'ÉVÉNEMENT N'A QU'UNE LANGUE (#550). Une liste
                    d'un seul choix promet un changement qui n'existe pas, et le chevron
                    invite à l'ouvrir pour rien. Le panneau garde le sien, dans la barre
                    de l'organisateur : il dit dans quelle langue on rédige. */}
                      {languages.length > 1 && (
                        <span className="lang__w">
                          {/**
                           * ⚠️ IL N'ENGAGE RIEN EN MODE ORGANISATEUR. La barre porte son
                           * propre sélecteur, qui appelle `setLanguage` : LIRE dans une
                           * autre langue pour la rédiger. Celui-ci appelle `chooseLanguage`
                           * pour un visiteur : CHOISIR sa langue, et la retenir dans le
                           * navigateur ET sur le compte. S'il engageait aussi en mode
                           * organisateur, un organisateur qui traduit son site en six
                           * langues repartirait avec le chinois sur son compte, sur son
                           * propre site de participant, à chacune de ses visites.
                           */}
                          <select
                            className="lang"
                            value={scope.languageId}
                            aria-label={t("LANGUAGE", "Langue")}
                            onChange={(e) =>
                              (mode === "organiser"
                                ? setLanguage
                                : chooseLanguage)(Number(e.target.value))
                            }
                          >
                            {/* ⚠️ SON NOM, DANS SA LANGUE, ET NON SA CLÉ. « FR » et « EN » sont
                        les clés de rangement de la base : personne ne choisit sa langue
                        dans un code à deux lettres, surtout pas quelqu'un qui ne lit pas
                        celle affichée. Voir `settings/nomDeLangue.ts`. */}
                            {languages.map((l) => (
                              <option key={l.id} value={l.id}>
                                {languageName(l.short, l.name)}
                              </option>
                            ))}
                          </select>
                          {/* ⚠️ `pointer-events: none` DANS LA FEUILLE : posé au-dessus du `select`,
                      il en intercepterait sinon le clic sur sa propre surface. */}
                          <span className="lang__c" aria-hidden="true">
                            <Icon name="chevron" />
                          </span>
                        </span>
                      )}
                    </div>
                  </header>
                )}

                {/* THE BANNER — the design's `.band`, finally carried by a setting.
                A background rather than an <img>: it is cropped to the width it
                is given, which is what makes one file work from a phone to a wide
                screen. What is kept of it when it is cropped is the organiser's to
                say — see `site/Band.tsx`. Decorative, so nothing to announce.
                `hidden` and not a conditional render: `.band:not([hidden])` is
                how the design switches it on, and an absent node would take the
                organiser's highlight target with it. */}
                {/* ⚠️ ET ABSENT SUR UN ÉCRAN NU. Il avait échappé aux autres gardes
                `!bare` : la barre, les onglets, l'ourlet du bas et le bouton de réglage
                disparaissent sur l'écran de salle (les coordonnées, elles, ne sortent
                pas de l'accueil), la bannière restait.
                Un événement qui a rempli son image d'en-tête projetait donc
                180 pixels de communication en haut du mur de plénière, ce que le
                mode nu existe précisément pour retirer. Aucun panneau de réglage
                ne s'affiche là non plus, donc rien à garder pour le surligneur.
                ⚠️ ET SUR LA RÉGIE (#347), qui garde pourtant tout le reste du
                châssis : voir `hasBanner`. */}
                {hasBanner(pathname) && (
                  <Band
                    imageKey="FO_MEETING_V2_HEADER_IMAGE"
                    focusKey="FO_MEETING_V2_HEADER_IMAGE_FOCUS"
                    safeKey="FO_MEETING_V2_HEADER_IMAGE_SAFE"
                    sidesKey="FO_MEETING_V2_HEADER_IMAGE_SIDES"
                    heightKey="FO_MEETING_V2_HEADER_IMAGE_HEIGHT"
                    cfg="headerImage"
                  />
                )}

                {/* `data-tunnel` turns `.scroll` into the funnel layout on desktop:
                the breadcrumb as a sticky column, the form beside it, the footer
                pinned below. See `desktop.css`.

                ⚠️ THE FOOTER IS A DIRECT CHILD OF `.scroll`, not of the body. The
                design pins it with `.scroll[data-tunnel="1"] > .foot`, and `>`
                reads the DOM — nested one level deeper it never matched, and the
                footer became a cell of the two-column grid. Outside the funnel
                nothing moves: `.scroll__body` is `display: contents`, so the box
                tree is the same either way. */}
                <div
                  ref={scrollRef}
                  className="scroll"
                  data-tunnel={inFunnel ? "1" : undefined}
                >
                  <div className="scroll__body">
                    <Steps path={pathname} />

                    {switchedNotice}
                    {content}
                  </div>

                  {/* ⚠️ LES COORDONNÉES SONT CELLES DE L'ACCUEIL, ET DE LUI SEUL. Elles
                  se répétaient au bas de chaque écran : vingt champs d'un formulaire, puis
                  l'adresse du palais des congrès ; la confirmation, puis l'adresse du
                  palais des congrès. L'organisateur, les dates, le lieu et le contact sont
                  ce qu'on vient lire sur la page d'accueil, pas une signature à repasser
                  sous chaque étape du parcours. La règle se lit dans le registre — voir
                  `hasFooter` —, et elle couvre l'écran nu au passage : l'écran de salle
                  n'est pas l'accueil. */}
                  {hasFooter(pathname) && <Footer />}

                  {/* ⚠️ L'IMAGE ET LES MENTIONS, ELLES, FERMENT TOUS LES ÉCRANS — d'où un
                  montage à CÔTÉ du pied de page et non dedans. Une mention légale est due
                  partout, et une page qui s'arrête net sur son dernier champ n'a plus de
                  bas : c'est l'ourlet de l'écran, pas la fiche d'identité de l'événement.
                  Seul l'écran nu y échappe, comme au bandeau du haut — un mur de plénière
                  n'a pas d'ourlet légal. */}
                  {!bare && <FooterEnd />}
                </div>

                {/* ⚠️ HORS DE `.scroll`, ET APRÈS ELLE. La barre du bas est le menu du
                MOBILE : posée dans la zone qui défile, elle serait partie avec le
                contenu vers le haut. Le châssis la veut en dernier enfant du cadre,
                collée en bas — c'est ainsi que le prototype la pose. */}
                {!bare && <SiteTabbar />}
              </div>
            </div>

            {mode === "organiser" && (
              <Panel
                page={page}
                {...(readInstance ? { instance: readInstance } : {})}
              />
            )}
          </div>

          {mode === "organiser" && spot && (
            <SpotEditor
              element={spot}
              page={page}
              {...(readInstance ? { instance: readInstance } : {})}
              onKeep={cancelTimers}
              onRelease={release}
              onClose={() => {
                cancelTimers();
                setSpot(null);
              }}
            />
          )}

          {mode === "organiser" && recap && (
            <Overview onClose={() => setRecap(false)} />
          )}

          {mode === "visitor" && !bare && canConfigure && (
            <button
              type="button"
              className="enter"
              onClick={() => setMode("organiser")}
            >
              Configurer le site
            </button>
          )}
        </div>
        <Confirm
          open={asking}
          title={t("LEAVE_REGISTRATION_ASK", "Quitter l'inscription ?")}
          message={t(
            "LEAVE_REGISTRATION_WARNING",
            "Vos réponses ne sont pas encore enregistrées : elles le seront à la dernière étape. Si vous partez maintenant, il faudra les redonner.",
          )}
          confirmLabel={t("LEAVE_ANYWAY", "Quitter quand même")}
          cancelLabel={t("STAY", "Rester")}
          onConfirm={leave}
          onCancel={() => setAsking(false)}
          /* People click a logo idly. The prominent button must be the one that
           keeps their answers, not the one that throws them away. */
          emphasis="cancel"
        />
      </OrganiserViews>
    </ModeProvider>
  );
}
